import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Package, Search, AlertTriangle, CheckCircle, XCircle,
  ChevronLeft, ChevronRight, ShoppingCart, Filter, Printer, FileSpreadsheet,
} from 'lucide-react';
import { exportToCSV, printTable } from '../../lib/printUtils';

const PAGE_SIZE = 50;

const ALERTA_STYLES = {
  critico: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-950/30', border: 'border-red-200 dark:border-red-800', label: 'Crítico' },
  bajo: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-950/30', border: 'border-amber-200 dark:border-amber-800', label: 'Bajo Mínimo' },
  ok: { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-950/30', border: 'border-emerald-200 dark:border-emerald-800', label: 'OK' },
};

export default function StockInsumosPage() {
  const [items, setItems] = useState([]);
  const [resumen, setResumen] = useState({ critico: 0, bajo_minimo: 0, ok: 0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [alertaFilter, setAlertaFilter] = useState('todos');
  const [page, setPage] = useState(0);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', PAGE_SIZE);
      params.set('skip', page * PAGE_SIZE);
      if (search) params.set('q', search);
      if (alertaFilter !== 'todos') params.set('alerta', alertaFilter);
      const res = await api.get(`/bodega/stock-insumos?${params}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setResumen(res.data.resumen || { critico: 0, bajo_minimo: 0, ok: 0 });
    } catch (err) { console.error('caught:', err); } finally {
      setLoading(false);
    }
  }, [page, search, alertaFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  // Carlos (10/05/2026): imprimir y exportar Stock Insumos
  const imprimirInsumos = () => {
    if (!items.length) return;
    printTable({
      title: 'Stock de Insumos y EPP',
      subtitle: `${items.length} items · Filtro: ${alertaFilter}${search ? ` · Búsqueda: "${search}"` : ''} · ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'Total items', value: total },
        { label: 'Crítico', value: resumen.critico, color: '#b91c1c' },
        { label: 'Bajo mínimo', value: resumen.bajo_minimo, color: '#b45309' },
        { label: 'OK', value: resumen.ok, color: '#047857' },
      ],
      columns: [
        { key: 'codigo', label: 'Código', mono: true },
        { key: 'descripcion2', label: 'Descripción', format: (v, r) => v || r.descripcion || '' },
        { key: 'familia_nombre', label: 'Familia' },
        { key: 'unidad', label: 'Unidad', align: 'center' },
        { key: 'stock_actual', label: 'Stock', align: 'right', mono: true, format: (v) => Number(v) || 0 },
        { key: 'stock_minimo', label: 'Mín', align: 'right', mono: true, format: (v) => Number(v) || 0 },
        { key: 'stock_critico', label: 'Crít', align: 'right', mono: true, format: (v) => Number(v) || 0 },
        { key: 'stock_maximo', label: 'Máx', align: 'right', mono: true, format: (v) => Number(v) || 0 },
        { key: 'ultimo_precio', label: 'Últ. Precio', align: 'right', mono: true, format: (v) => v ? `$${Math.round(v).toLocaleString('es-CL')}` : '-' },
        { key: 'alerta', label: 'Alerta', align: 'center', format: (v) => ALERTA_STYLES[v]?.label || v },
      ],
      rows: items,
    });
  };

  const exportarInsumosExcel = () => {
    if (!items.length) return;
    exportToCSV({
      filename: 'stock_insumos_epp',
      title: 'CHIRINO STEEL · Stock de Insumos y EPP',
      summary: [
        { label: 'Total items en sistema', value: total },
        { label: 'Items en archivo', value: items.length },
        { label: 'Crítico', value: resumen.critico },
        { label: 'Bajo mínimo', value: resumen.bajo_minimo },
        { label: 'OK', value: resumen.ok },
        { label: 'Filtro alerta', value: alertaFilter },
        { label: 'Búsqueda', value: search || '-' },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'codigo', label: 'Código' },
        { key: 'descripcion2', label: 'Descripción', format: (v, r) => v || r.descripcion || '' },
        { key: 'familia_nombre', label: 'Familia' },
        { key: 'unidad', label: 'Unidad' },
        { key: 'stock_actual', label: 'Stock Actual', format: (v) => Number(v) || 0 },
        { key: 'stock_minimo', label: 'Stock Mínimo', format: (v) => Number(v) || 0 },
        { key: 'stock_critico', label: 'Stock Crítico', format: (v) => Number(v) || 0 },
        { key: 'stock_maximo', label: 'Stock Máximo', format: (v) => Number(v) || 0 },
        { key: 'punto_reposicion', label: 'Punto Reposición', format: (v) => Number(v) || 0 },
        { key: 'ultimo_precio', label: 'Último Precio (CLP)', format: (v) => v ? Math.round(v) : 0 },
        { key: 'alerta', label: 'Alerta', format: (v) => ALERTA_STYLES[v]?.label || v },
        { key: 'ubicacion', label: 'Ubicación' },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows: items,
    });
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-6" data-testid="stock-insumos-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Package className="h-6 w-6" /> Stock de Insumos y EPP
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          {total} materiales en bodega
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <button onClick={() => { setAlertaFilter('critico'); setPage(0); }}
          className={`p-4 rounded-xl border-2 text-left transition-all ${alertaFilter === 'critico' ? 'ring-2 ring-red-400' : ''} ${ALERTA_STYLES.critico.bg} ${ALERTA_STYLES.critico.border}`}
          data-testid="kpi-critico">
          <XCircle className="h-5 w-5 text-red-500 mb-1" />
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">{resumen.critico}</div>
          <div className="text-xs text-red-600/70">Stock Crítico</div>
        </button>
        <button onClick={() => { setAlertaFilter('bajo'); setPage(0); }}
          className={`p-4 rounded-xl border-2 text-left transition-all ${alertaFilter === 'bajo' ? 'ring-2 ring-amber-400' : ''} ${ALERTA_STYLES.bajo.bg} ${ALERTA_STYLES.bajo.border}`}
          data-testid="kpi-bajo">
          <AlertTriangle className="h-5 w-5 text-amber-500 mb-1" />
          <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{resumen.bajo_minimo}</div>
          <div className="text-xs text-amber-600/70">Bajo Mínimo</div>
        </button>
        <button onClick={() => { setAlertaFilter('ok'); setPage(0); }}
          className={`p-4 rounded-xl border-2 text-left transition-all ${alertaFilter === 'ok' ? 'ring-2 ring-emerald-400' : ''} ${ALERTA_STYLES.ok.bg} ${ALERTA_STYLES.ok.border}`}
          data-testid="kpi-ok">
          <CheckCircle className="h-5 w-5 text-emerald-500 mb-1" />
          <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{resumen.ok}</div>
          <div className="text-xs text-emerald-600/70">Stock OK</div>
        </button>
      </div>

      {/* Search */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Buscar por descripción o código..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            data-testid="search-stock"
          />
        </div>
        {alertaFilter !== 'todos' && (
          <Button variant="outline" onClick={() => { setAlertaFilter('todos'); setPage(0); }} data-testid="clear-filter">
            <Filter className="h-4 w-4 mr-1" /> Limpiar filtro
          </Button>
        )}
        <Button variant="outline" onClick={imprimirInsumos} data-testid="btn-print-stock-insumos" title="Imprimir">
          <Printer className="h-4 w-4 mr-1" /> Imprimir
        </Button>
        <Button variant="outline" onClick={exportarInsumosExcel} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="btn-excel-stock-insumos" title="Exportar a Excel">
          <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
        </Button>
      </div>

      {/* Table */}
      <div className="bg-card border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="stock-table">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-4 py-3 font-medium w-8"></th>
                  <th className="text-left px-4 py-3 font-medium">Material</th>
                  <th className="text-left px-4 py-3 font-medium">Familia</th>
                  <th className="text-right px-4 py-3 font-medium">Stock</th>
                  <th className="text-right px-4 py-3 font-medium">Mín</th>
                  <th className="text-right px-4 py-3 font-medium">Crít</th>
                  <th className="text-right px-4 py-3 font-medium">Máx</th>
                  <th className="text-right px-4 py-3 font-medium">Últ. Precio</th>
                  <th className="text-left px-4 py-3 font-medium">Unidad</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const style = ALERTA_STYLES[item.alerta] || ALERTA_STYLES.ok;
                  const Icon = style.icon;
                  return (
                    <tr key={idx} className={`border-b hover:bg-muted/30 ${item.alerta === 'critico' ? 'bg-red-50/50 dark:bg-red-950/10' : item.alerta === 'bajo' ? 'bg-amber-50/50 dark:bg-amber-950/10' : ''}`}
                      data-testid={`stock-row-${idx}`}>
                      <td className="px-4 py-2.5"><Icon className={`h-4 w-4 ${style.color}`} /></td>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-xs">{item.descripcion2 || item.descripcion}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{item.codigo}</div>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">{item.familia_nombre}</td>
                      <td className="px-4 py-2.5 text-right font-bold">{item.stock_actual}</td>
                      <td className="px-4 py-2.5 text-right text-muted-foreground">{item.stock_minimo}</td>
                      <td className="px-4 py-2.5 text-right text-muted-foreground">{item.stock_critico}</td>
                      <td className="px-4 py-2.5 text-right text-muted-foreground">{item.stock_maximo}</td>
                      <td className="px-4 py-2.5 text-right text-xs">
                        {item.ultimo_precio ? `$${Math.round(item.ultimo_precio).toLocaleString()}` : '-'}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">{item.unidad}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/30">
            <span className="text-xs text-muted-foreground">
              {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} de {total}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">{page + 1} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="next-page">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
